import { useEffect, useRef, type RefObject } from 'react';

interface UseSwipeBackOptions {
  /** Only listen for the gesture while true (e.g. a sub-page/menu is open). */
  enabled: boolean;
  /** Called once a qualifying left-to-right swipe completes (after the page has slid away). */
  onSwipeBack: () => void;
  /**
   * Attach listeners to this element instead of `document`, and slide this element with the finger.
   * Use this when the gesture should be scoped to a specific page/overlay (e.g. a modal that sits on
   * top of a surface with its own touch handling, like the Leaflet map).
   */
  targetRef?: RefObject<HTMLElement | null>;
  /** Distance in px from the left edge the touch must start within (the left half of the screen is always allowed). */
  edgeWidth?: number;
  /** Minimum horizontal distance in px that counts as a swipe when released slowly. */
  minDistance?: number;
}

const SLIDE_OUT_MS = 240;
const SPRING_BACK_MS = 220;
const EASE_OUT = 'cubic-bezier(0.22, 1, 0.36, 1)';
// A quick flick completes the swipe even when it was short.
const FLICK_VELOCITY = 0.45; // px per ms
const LOCK_DISTANCE = 8;

// One physical touch belongs to one hook: the innermost listener claims it so nested/stacked pages
// (e.g. a modal inside the profile overlay) can't both animate and both close.
const CLAIMED = '__swipeBackClaimed';

const isFullScreenFixed = (el: HTMLElement): boolean => {
  if (getComputedStyle(el).position !== 'fixed') return false;
  const r = el.getBoundingClientRect();
  return r.width >= window.innerWidth * 0.9 && r.height >= window.innerHeight * 0.9;
};

/** The page under the finger: the outermost full-screen fixed ancestor of the touch target. */
const findFixedPage = (start: HTMLElement | null): HTMLElement | null => {
  let found: HTMLElement | null = null;
  for (let el = start; el && el !== document.body; el = el.parentElement) {
    if (isFullScreenFixed(el)) found = el;
  }
  return found;
};

/** True when the touch began inside something that scrolls sideways and still has content to its left. */
const startsInsideScrolledCarousel = (start: HTMLElement | null): boolean => {
  for (let el = start; el && el !== document.body; el = el.parentElement) {
    if (el.scrollWidth > el.clientWidth + 2 && el.scrollLeft > 2) {
      const ox = getComputedStyle(el).overflowX;
      if (ox === 'auto' || ox === 'scroll') return true;
    }
  }
  return false;
};

const settle = (page: HTMLElement) => {
  page.style.transition = '';
  page.style.transform = '';
  page.style.boxShadow = '';
  page.style.willChange = '';
  page.style.opacity = '';
};

/**
 * iOS-style swipe-to-go-back. The page under the finger follows it 1:1 and slides off to the right,
 * revealing the page beneath it - which is already mounted, so it is there the moment the swipe starts.
 * Let go past a third of the screen (or with a flick) and the page finishes sliding away before
 * `onSwipeBack` runs; let go early and it springs back.
 *
 * Starts from the left half of the screen, ignores touches on range sliders (they own their horizontal
 * drag) and on carousels that are scrolled away from their left end, and only takes over once the
 * movement is clearly horizontal so vertical scrolling is never hijacked.
 */
export function useSwipeBack({
  enabled,
  onSwipeBack,
  targetRef,
  edgeWidth = 40,
  minDistance = 50,
}: UseSwipeBackOptions): void {
  const onSwipeBackRef = useRef(onSwipeBack);
  onSwipeBackRef.current = onSwipeBack;

  useEffect(() => {
    if (!enabled || typeof window === 'undefined') return;
    const el: HTMLElement | Document = targetRef ? targetRef.current ?? document : document;
    if (!el) return;

    let tracking = false;
    let dragging = false;
    let locked: 'x' | 'y' | null = null;
    let startX = 0;
    let startY = 0;
    let lastX = 0;
    let lastT = 0;
    let velocity = 0;
    let page: HTMLElement | null = null;
    let busy = false;

    const reset = () => {
      tracking = false;
      dragging = false;
      locked = null;
    };

    const handleTouchStart = (e: TouchEvent) => {
      if (busy || e.touches.length !== 1 || (e as any)[CLAIMED]) { reset(); return; }
      const target = e.target as HTMLElement | null;
      if (target?.closest('input[type="range"], [data-no-swipe-back]') || startsInsideScrolledCarousel(target)) { reset(); return; }
      const touch = e.touches[0];
      const effectiveEdgeWidth = Math.max(edgeWidth, window.innerWidth / 2);
      if (touch.clientX > effectiveEdgeWidth) { reset(); return; }
      (e as any)[CLAIMED] = true;
      tracking = true;
      dragging = false;
      locked = null;
      startX = lastX = touch.clientX;
      startY = touch.clientY;
      lastT = performance.now();
      velocity = 0;
      page = (targetRef ? (targetRef.current as HTMLElement | null) : null) ?? findFixedPage(target);
    };

    const handleTouchMove = (e: TouchEvent) => {
      if (!tracking) return;
      const touch = e.touches[0];
      if (!touch) return;
      const dx = touch.clientX - startX;
      const dy = touch.clientY - startY;
      if (!locked) {
        if (Math.abs(dx) < LOCK_DISTANCE && Math.abs(dy) < LOCK_DISTANCE) return;
        locked = Math.abs(dx) > Math.abs(dy) * 1.2 && dx > 0 ? 'x' : 'y';
        if (locked === 'y') { reset(); return; }
        dragging = true;
        if (page) {
          page.style.transition = 'none';
          page.style.willChange = 'transform';
          page.style.boxShadow = '-14px 0 36px rgba(0,0,0,0.5)';
        }
      }
      if (!dragging) return;
      if (e.cancelable) e.preventDefault();
      const now = performance.now();
      const dt = now - lastT;
      if (dt > 0) velocity = velocity * 0.6 + ((touch.clientX - lastX) / dt) * 0.4;
      lastX = touch.clientX;
      lastT = now;
      if (page) page.style.transform = `translate3d(${Math.max(0, dx)}px, 0, 0)`;
    };

    const finish = (e: TouchEvent) => {
      if (!tracking) return;
      const wasDragging = dragging;
      const touch = e.changedTouches[0];
      const dx = touch ? touch.clientX - startX : 0;
      const dy = touch ? touch.clientY - startY : 0;
      const target = page;
      reset();
      page = null;

      const complete = wasDragging && (dx > window.innerWidth * 0.33 || (velocity > FLICK_VELOCITY && dx > 24) || (!target && dx >= minDistance && Math.abs(dx) > Math.abs(dy)));
      if (!complete) {
        if (target && wasDragging) {
          target.style.transition = `transform ${SPRING_BACK_MS}ms ${EASE_OUT}`;
          target.style.transform = 'translate3d(0, 0, 0)';
          window.setTimeout(() => settle(target), SPRING_BACK_MS + 20);
        }
        return;
      }

      busy = true;
      const run = () => {
        onSwipeBackRef.current();
        busy = false;
        // If the page is still in the DOM (it swaps its content in place instead of unmounting), bring the
        // new content in from the left like a revealed page, rather than popping back to the start.
        if (target && target.isConnected) {
          target.style.transition = 'none';
          target.style.boxShadow = '';
          target.style.transform = 'translate3d(-18%, 0, 0)';
          target.style.opacity = '0.6';
          requestAnimationFrame(() => requestAnimationFrame(() => {
            target.style.transition = `transform ${SLIDE_OUT_MS}ms ${EASE_OUT}, opacity ${SLIDE_OUT_MS}ms ${EASE_OUT}`;
            target.style.transform = 'translate3d(0, 0, 0)';
            target.style.opacity = '1';
            window.setTimeout(() => settle(target), SLIDE_OUT_MS + 20);
          }));
        }
      };

      if (!target) { run(); return; }
      const remaining = Math.max(0.15, 1 - dx / window.innerWidth);
      const duration = Math.round(SLIDE_OUT_MS * remaining + 90);
      target.style.transition = `transform ${duration}ms ${EASE_OUT}`;
      target.style.transform = `translate3d(${window.innerWidth}px, 0, 0)`;
      window.setTimeout(run, duration);
    };

    const handleTouchCancel = () => {
      if (page && dragging) {
        const target = page;
        target.style.transition = `transform ${SPRING_BACK_MS}ms ${EASE_OUT}`;
        target.style.transform = 'translate3d(0, 0, 0)';
        window.setTimeout(() => settle(target), SPRING_BACK_MS + 20);
      }
      reset();
      page = null;
    };

    el.addEventListener('touchstart', handleTouchStart as EventListener, { passive: true });
    el.addEventListener('touchmove', handleTouchMove as EventListener, { passive: false });
    el.addEventListener('touchend', finish as EventListener, { passive: true });
    el.addEventListener('touchcancel', handleTouchCancel as EventListener, { passive: true });

    return () => {
      el.removeEventListener('touchstart', handleTouchStart as EventListener);
      el.removeEventListener('touchmove', handleTouchMove as EventListener);
      el.removeEventListener('touchend', finish as EventListener);
      el.removeEventListener('touchcancel', handleTouchCancel as EventListener);
      if (page) settle(page);
    };
  }, [enabled, targetRef, edgeWidth, minDistance]);
}
